import { useNavigate } from "react-router-dom";
import useUserInfo from "../../hooks/useUserInfo";
import TranslationKeys from "../../contexts/I18nContext/translationKeys";
import Translate, { TranslateHtml } from "../../components/Translation/Translate";
import { DataGrid } from "./components/DataGrid";
import React, { useCallback, useEffect, useMemo } from "react";
import { getDocuments } from "../../data/rest/documents";
import { DocumentsRecord } from "../../types/documents.interface";
import {
  getCurrentTaxForm,
  getDaysUntilExpiration,
  getRenewableForm,
  getTaxCenterAlertState,
  getTaxFormLabel,
  getWithholdingRate,
  resolveRequiredFormType,
} from "../../common/taxForms.utils";
import useExpiringTaxFormQuery from "../../data/useExpiringTaxFormQuery";
import useInvestorInfo from "../../hooks/useInvestorInfo";
import {
  useMediaQuery,
  WhmAlert,
  WhmAlertTitle,
  WhmBox,
  WhmButton,
  WhmCard,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { useTheme } from "@mui/material";
import { DataGridSkeleton } from "./components/DataGrid.skeleton";
import { useMFEContext } from "../../contexts/MFEContext";
import "../../scss/styles.scss";
import TaxForm from "../TaxForm";
import { ErrorPage } from "../../components/shared/ErrorPage";
import SeasonBanner from "../../components/shared/SeasonBanner";
import TaxFormSubmittedModal, {
  getSubmittedTaxFormType,
} from "./components/TaxFormSubmittedModal";

type AlertConfig = {
  severity: "warning" | "info";
  titleKey: (typeof TranslationKeys)[keyof typeof TranslationKeys];
  messageKey: (typeof TranslationKeys)[keyof typeof TranslationKeys];
  ctaPath?: string;
  replacements?: Record<string, string | number>;
};

const NO_FORM_ALERT: AlertConfig = {
  severity: "warning",
  titleKey: TranslationKeys.TAX_CENTER_ALERT_TITLE,
  messageKey: TranslationKeys.TAX_CENTER_ALERT_MESSAGE,
  ctaPath: "/tax-center/tax-form",
};

const formatAlertDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const Dashboard = () => {
  const [documents, setDocuments] = React.useState<DocumentsRecord[] | null>(
    null,
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<boolean>(false);
  const [submittedFormType, setSubmittedFormType] = React.useState(
    getSubmittedTaxFormType,
  );
  const { basePath, taxFormsExpirationsEnabled = false } = useMFEContext();
  const userInfo = useUserInfo();
  const navigate = useNavigate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { expiringTaxForm } = useExpiringTaxFormQuery(
    taxFormsExpirationsEnabled,
  );

  const alertState = useMemo(
    () =>
      getTaxCenterAlertState(
        documents ?? [],
        expiringTaxForm?.isExpiringSoon ?? false,
      ),
    [documents, expiringTaxForm],
  );

  const isNoForm = alertState === "no-form";
  const { hasUSTax, hasForeignTax, isEntity } = useInvestorInfo(
    isNoForm && taxFormsExpirationsEnabled,
  );
  const requiredFormType = useMemo(
    () =>
      isNoForm
        ? resolveRequiredFormType(hasUSTax, hasForeignTax, isEntity)
        : undefined,
    [isNoForm, hasUSTax, hasForeignTax, isEntity],
  );

  const renewableForm = useMemo(
    () => getRenewableForm(documents ?? []),
    [documents],
  );

  const currentForm = useMemo(
    () => getCurrentTaxForm(documents ?? []),
    [documents],
  );

  const alert = useMemo<AlertConfig | null>(() => {
    // Flag off: only the "no tax form" banner, as it was before the epic
    if (!taxFormsExpirationsEnabled) {
      return alertState === "no-form" ? NO_FORM_ALERT : null;
    }
    if (alertState === "under-review" && currentForm?.submissionDate) {
      const replacements: Record<string, string | number> = {
        formType: getTaxFormLabel(currentForm.documentType),
        submissionDate: formatAlertDate(currentForm.submissionDate),
      };
      return {
        severity: "info",
        titleKey: TranslationKeys.TAX_CENTER_ALERT_UNDER_REVIEW_TITLE,
        messageKey: TranslationKeys.TAX_CENTER_ALERT_UNDER_REVIEW_MESSAGE,
        replacements,
      };
    }
    if (alertState === "invalid" && currentForm) {
      const replacements: Record<string, string | number> = {
        formType: getTaxFormLabel(currentForm.documentType),
        withholdingRate: getWithholdingRate(currentForm.documentType),
      };
      if (currentForm.rejectionReason) {
        replacements.rejectionReason = currentForm.rejectionReason;
      }
      return {
        severity: "warning",
        titleKey: TranslationKeys.TAX_CENTER_ALERT_TYPED_TITLE,
        messageKey: currentForm.rejectionReason
          ? TranslationKeys.TAX_CENTER_ALERT_INVALID_WITH_REASON_MESSAGE
          : TranslationKeys.TAX_CENTER_ALERT_INVALID_MESSAGE,
        ctaPath: "/tax-center/tax-form?mode=renew",
        replacements,
      };
    }
    if (
      alertState === "expiring-soon" &&
      renewableForm &&
      expiringTaxForm?.expirationDate
    ) {
      const expirationDate = new Date(expiringTaxForm.expirationDate);
      const replacements: Record<string, string | number> = {
        formType: getTaxFormLabel(renewableForm.documentType),
        expirationDate: formatAlertDate(expirationDate),
        daysUntilExpiration: getDaysUntilExpiration(expirationDate),
      };
      return {
        severity: "warning",
        titleKey: TranslationKeys.TAX_CENTER_ALERT_TYPED_TITLE,
        messageKey: TranslationKeys.TAX_CENTER_ALERT_EXPIRING_SOON_MESSAGE,
        ctaPath: "/tax-center/tax-form?mode=renew",
        replacements,
      };
    }
    if (alertState === "expired" && renewableForm?.expirationDate) {
      const replacements: Record<string, string | number> = {
        formType: getTaxFormLabel(renewableForm.documentType),
        expirationDate: formatAlertDate(renewableForm.expirationDate),
      };
      return {
        severity: "warning",
        titleKey: TranslationKeys.TAX_CENTER_ALERT_TYPED_TITLE,
        messageKey: TranslationKeys.TAX_CENTER_ALERT_EXPIRED_MESSAGE,
        ctaPath: "/tax-center/tax-form?mode=renew",
        replacements,
      };
    }
    if (alertState === "no-form") {
      return requiredFormType
        ? {
            severity: "warning",
            titleKey: TranslationKeys.TAX_CENTER_ALERT_TYPED_TITLE,
            messageKey: TranslationKeys.TAX_CENTER_ALERT_NO_FORM_TYPED_MESSAGE,
            ctaPath: "/tax-center/tax-form",
            replacements: { formType: getTaxFormLabel(requiredFormType) },
          }
        : NO_FORM_ALERT;
    }
    return null;
  }, [
    taxFormsExpirationsEnabled,
    alertState,
    currentForm,
    renewableForm,
    expiringTaxForm,
    requiredFormType,
  ]);

  useEffect(() => {
    void loadServerRows();
  }, []);

  const loadServerRows = useCallback(async () => {
    try {
      const data = await getDocuments(userInfo);
      setDocuments(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  if (error) return <ErrorPage />;
  if (documents && !documents.length) return <TaxForm />;

  return (
    <WhmCard
      variant="outlined"
      sx={{
        marginTop: "32px",
        backgroundColor: isMobile ? "#f7f7f7" : "#ffffff",
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingBottom: "8px",
        paddingTop: alert && !loading ? "16px" : "8px",
        ...(isMobile && { border: 0 }),
      }}
    >
      <>
        <SeasonBanner />
        {alert && !loading && (
          <WhmAlert
            severity={alert.severity}
            variant={"standard"}
            sx={{
              marginBottom: "16px",
              ".MuiAlert-action": { marginRight: 0 },
            }}
            action={
              !isMobile &&
              alert.ctaPath && (
                <WhmButton
                  color={"secondary"}
                  size={"medium"}
                  variant={"contained"}
                  sx={{
                    marginBottom: "0",
                    height: "auto !important",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                  onClick={() => navigate(`${basePath}${alert.ctaPath}`)}
                >
                  <Translate
                    translationKey={TranslationKeys.TAX_CENTER_ALERT_ACTION}
                  />
                </WhmButton>
              )
            }
          >
            <WhmBox sx={{ flexGrow: 1 }}>
              <WhmAlertTitle>
                <Translate
                  translationKey={alert.titleKey}
                  replacements={alert.replacements}
                />
              </WhmAlertTitle>
              <WhmTypography variant="body2">
                <TranslateHtml
                  translationKey={alert.messageKey}
                  parameters={alert.replacements}
                />
              </WhmTypography>
            </WhmBox>
            {isMobile && alert.ctaPath && (
              <WhmButton
                color={"secondary"}
                size={"medium"}
                variant={"contained"}
                sx={{ marginTop: "8px", width: "100%" }}
                onClick={() => navigate(`${basePath}${alert.ctaPath}`)}
              >
                <Translate
                  translationKey={TranslationKeys.TAX_CENTER_ALERT_ACTION}
                />
              </WhmButton>
            )}
          </WhmAlert>
        )}
        {taxFormsExpirationsEnabled && submittedFormType && !loading && (
          <TaxFormSubmittedModal
            formType={submittedFormType}
            expirationDate={
              currentForm?.documentType === submittedFormType
                ? currentForm.expirationDate
                : undefined
            }
            onClose={() => setSubmittedFormType(null)}
          />
        )}
        {loading ? (
          <DataGridSkeleton />
        ) : (
          <DataGrid
            documents={documents ?? []}
            /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
            loadServerRows={loadServerRows}
          />
        )}
      </>
    </WhmCard>
  );
};

export default Dashboard;
